import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback"?: () => void;
        },
      ) => string;
      reset?: (widgetId?: string) => void;
    };
  }
}

const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";

export function TurnstileWidget(props: { onToken: (token: string | undefined) => void }) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const onTokenRef = useRef(props.onToken);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    onTokenRef.current = props.onToken;
  }, [props.onToken]);

  useEffect(() => {
    if (!siteKey || !container) {
      onTokenRef.current(undefined);
      return;
    }

    if (widgetIdRef.current) {
      return;
    }

    if (!document.getElementById(TURNSTILE_SCRIPT_ID)) {
      const script = document.createElement("script");
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    const interval = window.setInterval(() => {
      if (!window.turnstile) {
        return;
      }

      window.clearInterval(interval);
      widgetIdRef.current = window.turnstile.render(container, {
        sitekey: siteKey,
        callback: (token) => onTokenRef.current(token),
        "expired-callback": () => onTokenRef.current(undefined),
      });
    }, 150);

    return () => window.clearInterval(interval);
  }, [container, siteKey]);

  if (!siteKey) {
    return null;
  }

  return <div className="turnstile-shell" ref={setContainer} />;
}
